import {
  ArrowRight,
  BookOpenCheck,
  BrainCircuit,
  CheckCircle2,
  CircleDot,
  ClipboardCheck,
  Filter,
  GraduationCap,
  RotateCcw,
  Search,
  Sparkles
} from "lucide-react";
import { useMemo, useState } from "react";
import {
  byId,
  cleanText,
  domainsForSubject,
  findCluster,
  getPrerequisites,
  getUnlocks,
  manifest,
  subjects,
  suggestTopics,
  topics,
  type ProgressState,
  type Topic
} from "./curriculum";
import { useProgress } from "./useProgress";

const stateLabels: Record<ProgressState, string> = {
  "not-started": "Not started",
  learning: "Learning",
  mastered: "Mastered"
};

const stateOrder: ProgressState[] = ["not-started", "learning", "mastered"];

export default function App() {
  const [query, setQuery] = useState("");
  const [subject, setSubject] = useState("All");
  const [domain, setDomain] = useState("All");
  const [age, setAge] = useState("All");
  const [selectedId, setSelectedId] = useState("mt_AzTrT5ySCx");
  const { progress, setTopicState, resetProgress } = useProgress();

  const selected = byId.get(selectedId) ?? topics[0];
  const domains = useMemo(() => domainsForSubject(subject), [subject]);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();

    return topics
      .filter((topic) => subject === "All" || topic.subject === subject)
      .filter((topic) => domain === "All" || topic.domain === domain)
      .filter((topic) => age === "All" || topic.ageRangeStart === Number(age))
      .filter((topic) => {
        if (!normalized) return true;
        const haystack = `${topic.name} ${topic.description} ${topic.subject} ${topic.domain}`.toLowerCase();
        return haystack.includes(normalized);
      })
      .sort((a, b) => b.centrality - a.centrality)
      .slice(0, 80);
  }, [age, domain, query, subject]);

  const progressCounts = useMemo(() => {
    const values = Object.values(progress);
    return {
      learning: values.filter((value) => value === "learning").length,
      mastered: values.filter((value) => value === "mastered").length
    };
  }, [progress]);

  function selectTopic(topic: Topic) {
    setSelectedId(topic.id);
  }

  return (
    <main className="app-shell">
      <section className="workspace">
        <aside className="sidebar" aria-label="Curriculum search">
          <div className="brand-row">
            <div className="brand-mark" aria-hidden="true">
              <BrainCircuit size={22} />
            </div>
            <div>
              <p className="eyebrow">Marble MVP</p>
              <h1>Curriculum Studio</h1>
            </div>
          </div>

          <div className="metric-strip" aria-label="Dataset summary">
            <Metric value={manifest.counts.topics} label="topics" />
            <Metric value={manifest.counts.dependencies} label="links" />
            <Metric value={manifest.counts.curricula} label="sources" />
          </div>

          <label className="search-box">
            <Search size={18} aria-hidden="true" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search topics, skills, domains"
            />
          </label>

          <div className="filters" aria-label="Filters">
            <label>
              <Filter size={16} aria-hidden="true" />
              <select
                value={subject}
                onChange={(event) => {
                  setSubject(event.target.value);
                  setDomain("All");
                }}
              >
                <option>All</option>
                {subjects.map((item) => (
                  <option key={item}>{item}</option>
                ))}
              </select>
            </label>
            <label>
              <CircleDot size={16} aria-hidden="true" />
              <select value={domain} onChange={(event) => setDomain(event.target.value)}>
                <option>All</option>
                {domains.map((item) => (
                  <option key={item}>{item}</option>
                ))}
              </select>
            </label>
            <label>
              <GraduationCap size={16} aria-hidden="true" />
              <select value={age} onChange={(event) => setAge(event.target.value)}>
                <option>All</option>
                {[5, 6, 7, 8, 9, 10, 11, 12, 13].map((item) => (
                  <option key={item} value={item}>
                    Age {item}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="result-list" aria-label="Search results">
            {filtered.map((topic) => (
              <button
                className={`topic-row ${selected.id === topic.id ? "active" : ""}`}
                key={topic.id}
                onClick={() => selectTopic(topic)}
              >
                <span className={`state-dot ${progress[topic.id] ?? "not-started"}`} />
                <span>
                  <strong>{topic.name}</strong>
                  <small>
                    {topic.subject} / {topic.domain} / {topic.ageRangeStart}-{topic.ageRangeEnd}
                  </small>
                </span>
              </button>
            ))}
          </div>
        </aside>

        <section className="detail-pane">
          <header className="topbar">
            <div>
              <p className="eyebrow">Learner progress</p>
              <h2>
                {progressCounts.mastered} mastered, {progressCounts.learning} learning
              </h2>
            </div>
            <button className="icon-button" onClick={resetProgress} title="Reset progress">
              <RotateCcw size={18} />
            </button>
          </header>

          <TopicHero
            topic={selected}
            state={progress[selected.id] ?? "not-started"}
            setState={(state) => setTopicState(selected.id, state)}
          />

          <section className="content-grid">
            <TopicGraph topic={selected} onSelect={selectTopic} />
            <AssessmentPanel
              topic={selected}
              state={progress[selected.id] ?? "not-started"}
              setState={(state) => setTopicState(selected.id, state)}
            />
          </section>

          <RecommendationPanel progress={progress} onSelect={selectTopic} />
        </section>
      </section>
    </main>
  );
}

function Metric({ value, label }: { value: number; label: string }) {
  return (
    <span>
      <strong>{value.toLocaleString()}</strong>
      <small>{label}</small>
    </span>
  );
}

function TopicHero({
  topic,
  state,
  setState
}: {
  topic: Topic;
  state: ProgressState;
  setState: (state: ProgressState) => void;
}) {
  const cluster = findCluster(topic);

  return (
    <section className="topic-hero">
      <div>
        <p className="eyebrow">
          {topic.subject} / {topic.domain} / Age {topic.ageRangeStart}-{topic.ageRangeEnd}
        </p>
        <h2>{topic.name}</h2>
        <p>{cleanText(topic.description)}</p>
        {cluster ? <small>{cleanText(cluster.summary)}</small> : null}
      </div>
      <div className="state-control" aria-label="Topic progress">
        {stateOrder.map((item) => (
          <button
            key={item}
            className={state === item ? "selected" : ""}
            onClick={() => setState(item)}
          >
            {stateLabels[item]}
          </button>
        ))}
      </div>
    </section>
  );
}

function TopicGraph({
  topic,
  onSelect
}: {
  topic: Topic;
  onSelect: (topic: Topic) => void;
}) {
  const prerequisites = getPrerequisites(topic.id).slice(0, 5);
  const unlocks = getUnlocks(topic.id).slice(0, 5);

  return (
    <section className="panel graph-panel">
      <div className="panel-header">
        <div>
          <p className="eyebrow">Learning map</p>
          <h3>Prerequisites and unlocks</h3>
        </div>
        <Sparkles size={20} aria-hidden="true" />
      </div>

      <div className="graph-lane" aria-label="Topic dependency graph">
        <div className="node-column">
          <h4>Before</h4>
          {prerequisites.length ? (
            prerequisites.map(({ topic: item, dependency }) => (
              <button key={item.id} className="mini-node" onClick={() => onSelect(item)}>
                <strong>{item.name}</strong>
                <small>{dependency.strength} prerequisite</small>
              </button>
            ))
          ) : (
            <p className="empty-copy">No listed prerequisites.</p>
          )}
        </div>

        <div className="center-node" aria-label="Selected topic">
          <BookOpenCheck size={24} />
          <strong>{topic.name}</strong>
          <small>{topic.subject}</small>
        </div>

        <div className="node-column">
          <h4>Next</h4>
          {unlocks.length ? (
            unlocks.map(({ topic: item, dependency }) => (
              <button key={item.id} className="mini-node" onClick={() => onSelect(item)}>
                <strong>{item.name}</strong>
                <small>{dependency.strength} unlock</small>
              </button>
            ))
          ) : (
            <p className="empty-copy">No listed unlocks.</p>
          )}
        </div>
      </div>
    </section>
  );
}

function AssessmentPanel({
  topic,
  state,
  setState
}: {
  topic: Topic;
  state: ProgressState;
  setState: (state: ProgressState) => void;
}) {
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const evidenceCount = topic.evidence.length || 1;
  const achieved = topic.evidence.filter((item) => checked[item]).length;
  const score = Math.round((achieved / evidenceCount) * 100);

  function toggle(item: string) {
    setChecked((current) => ({ ...current, [item]: !current[item] }));
  }

  function applyResult() {
    if (score >= 80) setState("mastered");
    else if (score > 0) setState("learning");
    else setState("not-started");
  }

  return (
    <section className="panel assessment-panel">
      <div className="panel-header">
        <div>
          <p className="eyebrow">Assessment</p>
          <h3>Evidence check</h3>
        </div>
        <ClipboardCheck size={20} aria-hidden="true" />
      </div>

      {topic.assessmentPrompt ? (
        <p className="prompt">{cleanText(topic.assessmentPrompt.replace("{{name}}", "the learner"))}</p>
      ) : null}

      <div className="checklist">
        {topic.evidence.map((item) => (
          <label key={item}>
            <input
              type="checkbox"
              checked={Boolean(checked[item])}
              onChange={() => toggle(item)}
            />
            <span>{cleanText(item)}</span>
          </label>
        ))}
      </div>

      <div className="assessment-footer">
        <div className={`score-ring ${score >= 80 ? "strong" : score > 0 ? "mid" : ""}`}>
          {score}%
        </div>
        <button className="primary-button" onClick={applyResult}>
          <CheckCircle2 size={18} />
          Save as {score >= 80 ? "mastered" : score > 0 ? "learning" : "not started"}
        </button>
      </div>

      <small className="status-note">Current status: {stateLabels[state]}</small>
    </section>
  );
}

function RecommendationPanel({
  progress,
  onSelect
}: {
  progress: Record<string, ProgressState>;
  onSelect: (topic: Topic) => void;
}) {
  const suggestions = suggestTopics(progress);

  return (
    <section className="recommendations">
      <div className="panel-header">
        <div>
          <p className="eyebrow">Next best topics</p>
          <h3>Suggested from the graph</h3>
        </div>
        <ArrowRight size={20} aria-hidden="true" />
      </div>
      <div className="suggestion-grid">
        {suggestions.map(({ topic, masteredPrereqs, totalHardPrereqs }) => (
          <button key={topic.id} className="suggestion" onClick={() => onSelect(topic)}>
            <strong>{topic.name}</strong>
            <span>
              {topic.subject} / Age {topic.ageRangeStart}-{topic.ageRangeEnd}
            </span>
            <small>
              {masteredPrereqs}/{totalHardPrereqs} hard prerequisites mastered
            </small>
          </button>
        ))}
      </div>
    </section>
  );
}
